import React, { useState, useEffect } from 'react';
import { 
  FiDownload, FiMail, FiPrinter, FiCalendar, FiUsers, 
  FiDollarSign, FiBriefcase, FiAlertCircle, FiCheckCircle, FiEye
} from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function ExportDataPage() {
  const [dataType, setDataType] = useState('revenue'); // revenue, jobs, employees
  const [startDate, setStartDate] = useState('2026-06-01');
  const [endDate, setEndDate] = useState('2026-06-30');
  const [recipientEmail, setRecipientEmail] = useState(
    localStorage.getItem('profile_email') || 'owner@thundermaxhome.com'
  );
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [dataType]);

  const loadData = () => {
    setIsLoading(true);
    let action = 'get_all_jobs';
    if (dataType === 'employees') {
      action = 'get_employees';
    } else if (dataType === 'revenue') {
      action = 'get_all_jobs';
    }
    
    fetch(`${API_URL}?action=${action}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data) {
          if (dataType === 'revenue') {
            setDataList(data.data.filter(j => j.status === 'completed'));
          } else {
            setDataList(data.data);
          }
        } else {
          setDataList([]);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setDataList([]);
        setIsLoading(false);
      });
  };

  const getFilteredData = () => {
    if (dataType === 'employees') return dataList;
    return dataList.filter(item => {
      const itemDate = item.appointmentDate || item.createdAt?.split(' ')[0];
      if (!itemDate) return false;
      return itemDate >= startDate && itemDate <= endDate;
    });
  };

  const filteredItems = getFilteredData();

  // 📝 ฟังก์ชันสร้างหัวข้อจดหมายอีเมล (Subject)
  const getEmailSubject = () => {
    const types = {
      'revenue': 'สรุปข้อมูลรายรับจริงสะสม',
      'jobs': 'รายงานข้อมูลคิวงานช่างและลูกค้า',
      'employees': 'ทำเนียบรายชื่อบุคลากรและสิทธิ์ใช้งาน'
    };
    const range = dataType === 'employees' ? 'ล่าสุด' : `${startDate} ถึง ${endDate}`;
    return `[รายงานฝ่ายบริหาร] ${types[dataType]} ประจำช่วงเวลา ${range}`;
  };

  // 📝 ฟังก์ชันสร้างข้อความจดหมายอีเมล (Body Text)
  const getEmailBodyText = () => {
    const types = {
      'revenue': 'รายงานแจกแจงรายรับจริงที่ปิดงานเรียบร้อยแล้ว (Completed Revenue)',
      'jobs': 'รายงานคิวงานซ่อม/ติดตั้ง และสถานะความคืบหน้าของช่างทั้งหมด',
      'employees': 'โครงสร้างแผนกและทำเนียบพนักงานในระบบปฏิบัติการ'
    };

    let summarySection = '';
    if (dataType === 'revenue') {
      const totalSum = filteredItems.reduce((sum, j) => sum + (Number(j.price) || 0), 0);
      summarySection = `\n- จำนวนยอดที่จบงานสำเร็จ: ${filteredItems.length} คิวงาน\n- รวมรายรับจริงสะสมสุทธิ: ฿${totalSum.toLocaleString()} บาท\n- หักส่วนแบ่งค่าคอมมิชชันฝ่ายขาย (5%): ฿${(totalSum * 0.05).toLocaleString()} บาท`;
    } else if (dataType === 'jobs') {
      const pendingCount = filteredItems.filter(j => j.status === 'pending_approval').length;
      const completedCount = filteredItems.filter(j => j.status === 'completed').length;
      summarySection = `\n- จำนวนคิวงานบันทึกในช่วงเวลา: ${filteredItems.length} รายการ\n- รายการรอแอดมินยืนยัน: ${pendingCount} คิว\n- รายการล้างแอร์เสร็จสมบูรณ์: ${completedCount} คิว`;
    } else {
      summarySection = `\n- จำนวนรายชื่อพนักงานสะสม: ${filteredItems.length} คน`;
    }

    return `เรียน ท่านผู้บริหารและเจ้าของระบบ (Owner),

ระบบสารสนเทศอัตโนมัติ Thunder CRM Engine ได้ทำการสรุปเอกสารรายงาน ข้อมูลบริษัท เรียบร้อยแล้วครับ

รายละเอียดข้อมูลนำส่ง:
---------------------------------------------
* หัวข้อรายงาน: ${types[dataType]}
* ช่วงเวลาประมวลผล: ${dataType === 'employees' ? 'อัปเดต ณ ปัจจุบัน' : `${startDate} ถึง ${endDate}`}
* ดึงรายงานเมื่อเวลา: ${new Date().toLocaleString('th-TH', { hour12: false })}
* สรุปตัวเลขสถิติเบื้องต้น:${summarySection}
---------------------------------------------

ไฟล์รายงานที่แนบมากับอีเมลนี้ (Attachments):
1. report_${dataType}_data.csv (เอกสารรูปแบบ Excel สำหรับวิเคราะห์สเปรดชีต)
2. report_${dataType}_data.pdf (รายงาน PDF สรุปตารางสำหรับใช้พิมพ์กระดาษ A4)

ท่านสามารถเข้าทำการตรวจสอบ ค้นหา และควบคุมการเข้าถึงระบบได้โดยตรงที่หน้าแดชบอร์ดผู้บริหารได้ตลอด 24 ชั่วโมง

ขอแสดงความนับถืออย่างสูง,
ระบบสารสนเทศอัตโนมัติ Thunder CRM Engine
บริษัท ธันเดอร์ แมกซ์โฮม จำกัด (สำนักงานใหญ่)`;
  };

  const generateCSVString = () => {
    let csvContent = "\uFEFF"; // UTF-8 BOM
    if (dataType === 'revenue') {
      csvContent += "รหัสงาน,ชื่อลูกค้า,บริการ/อาการ,ประเภทงาน,ราคา (บาท),ช่างที่รับผิดชอบ,วันที่นัดหมาย\n";
      filteredItems.forEach(j => {
        csvContent += `"${j.id}","${j.customerName}","${j.problem}","${j.job_type}","${j.price}","${j.techName || '-'}","${j.appointmentDate}"\n`;
      });
    } else if (dataType === 'jobs') {
      csvContent += "รหัสงาน,ชื่อลูกค้า,เบอร์โทร,บริการ/อาการ,ประเภทงาน,สถานะ,ช่างที่รับผิดชอบ,วันที่นัดหมาย\n";
      filteredItems.forEach(j => {
        csvContent += `"${j.id}","${j.customerName}","${j.phone}","${j.problem}","${j.job_type}","${j.status}","${j.techName || '-'}","${j.appointmentDate}"\n`;
      });
    } else {
      csvContent += "รหัสพนักงาน,ชื่อพนักงาน,ชื่อผู้ใช้,ตำแหน่ง,สิทธิ์ความปลอดภัย,เบอร์โทร\n";
      filteredItems.forEach(e => {
        csvContent += `"${e.id}","${e.name}","${e.username}","${e.role}","${e.permissions ? 'กำหนดสิทธิ์แล้ว' : 'ทั่วไป'}","${e.phone || '-'}"\n`;
      });
    }
    return csvContent;
  };

  const handleExportExcel = () => {
    if (filteredItems.length === 0) {
      Swal.fire('คำเตือน', 'ไม่พบข้อมูลในช่วงเวลาที่ระบุ', 'warning');
      return;
    }

    const csvContent = generateCSVString();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `report_${dataType}_${startDate}_to_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    Swal.fire('สำเร็จ', 'ดาวน์โหลดไฟล์ Excel (.csv) เรียบร้อยแล้ว', 'success');
  };

  const handlePrint = () => {
    if (filteredItems.length === 0) {
      Swal.fire('คำเตือน', 'ไม่พบข้อมูลในการสั่งพิมพ์', 'warning');
      return;
    }

    const printWindow = window.open('', '_blank');
    let tableHtml = `<table border="1" style="width:100%; border-collapse:collapse; font-family:Sarabun,sans-serif; font-size:12px;">`;
    
    if (dataType === 'revenue') {
      tableHtml += `<thead><tr style="background:#f2f2f2;"><th>รหัส</th><th>ชื่อลูกค้า</th><th>บริการ</th><th>ราคา</th><th>ช่าง</th><th>วันที่</th></tr></thead><tbody>`;
      filteredItems.forEach(j => {
        tableHtml += `<tr><td>${j.id}</td><td>${j.customerName}</td><td>${j.problem}</td><td>฿${Number(j.price).toLocaleString()}</td><td>${j.techName || '-'}</td><td>${j.appointmentDate}</td></tr>`;
      });
    } else if (dataType === 'jobs') {
      tableHtml += `<thead><tr style="background:#f2f2f2;"><th>รหัส</th><th>ชื่อลูกค้า</th><th>เบอร์โทร</th><th>บริการ</th><th>สถานะ</th><th>วันที่นัด</th></tr></thead><tbody>`;
      filteredItems.forEach(j => {
        tableHtml += `<tr><td>${j.id}</td><td>${j.customerName}</td><td>${j.phone}</td><td>${j.problem}</td><td>${j.status}</td><td>${j.appointmentDate}</td></tr>`;
      });
    } else {
      tableHtml += `<thead><tr style="background:#f2f2f2;"><th>รหัส</th><th>ชื่อพนักงาน</th><th>ชื่อผู้ใช้</th><th>ตำแหน่ง</th><th>เบอร์โทร</th></tr></thead><tbody>`;
      filteredItems.forEach(e => {
        tableHtml += `<tr><td>${e.id}</td><td>${e.name}</td><td>${e.username}</td><td>${e.role}</td><td>${e.phone || '-'}</td></tr>`;
      });
    }
    tableHtml += `</tbody></table>`;

    const htmlContent = `
      <html>
            p { font-size: 11px; margin-bottom: 20px; color: #666; }
            th, td { padding: 8px; text-align: left; }
          </style>
        </head>
        <body onload="window.print();window.close();">
          <h1>รายงานข้อมูล${dataType === 'revenue' ? 'รายรับจริง' : dataType === 'jobs' ? 'คิวงานทั้งหมด' : 'พนักงานบริษัท'}</h1>
          <p>ช่วงเวลาวันที่: ${startDate} ถึง ${endDate} | พิมพ์เมื่อ: ${new Date().toLocaleString()}</p>
          ${tableHtml}
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (filteredItems.length === 0) {
      Swal.fire('คำเตือน', 'ไม่พบข้อมูลในช่วงเวลาที่ระบุสำหรับส่งอีเมล', 'warning');
      return;
    }

    Swal.fire({
      title: 'กำลังส่งข้อมูลเข้าอีเมล...',
      text: `กำลังประมวลผลรายงานเพื่อจัดส่งไปยัง ${recipientEmail}`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    const csvContent = generateCSVString();
    const filename = `report_${dataType}_${startDate}_to_${endDate}.csv`;

    try {
      const res = await fetch(`${API_URL}?action=send_report_email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: recipientEmail,
          subject: getEmailSubject(),
          body: getEmailBodyText(),
          csvContent: csvContent,
          filename: filename
        })
      });
      const result = await res.json();
      if (result.success) {
        Swal.fire({
          title: 'ส่งอีเมลรายงานสำเร็จ!',
          html: `<div class="text-left text-xs leading-relaxed space-y-1">
            <p>จัดส่งรายงานหมวดหมู่: <span class="font-bold text-indigo-500">${dataType === 'revenue' ? 'รายรับจริง' : dataType === 'jobs' ? 'คิวงานทั้งหมด' : 'พนักงาน'}</span></p>
            <p>ไปยังปลายทาง: <span class="font-bold text-indigo-500">${recipientEmail}</span></p>
            <p class="text-[10px] text-gray-400 mt-2 font-mono font-bold text-emerald-600">ส่งอีเมลพร้อมไฟล์แนบรายงาน (.csv) เรียบร้อยแล้ว</p>
          </div>`,
          icon: 'success'
        });
      } else {
        Swal.fire('เกิดข้อผิดพลาด', result.message || 'ไม่สามารถส่งรายงานเข้าอีเมลได้', 'error');
      }
    } catch (error) {
      Swal.fire('ข้อผิดพลาด', 'ไม่สามารถเชื่อมต่อระบบส่งอีเมลได้', 'error');
    }
  };

  return (
    <div className="space-y-6 pb-20 animate-fade-in text-gray-800 dark:text-gray-105 font-sans text-left">
      
      {/* Header */}
      <div>
        <h1 className="text-xl font-black text-gray-850 dark:text-white flex items-center gap-2">
          📥 ระบบส่งออกรายงานและข้อมูลหลัก (Data Export Console)
        </h1>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          ดึงรายงานรายรับ รายการช่างล้างแอร์ หรือข้อมูลพนักงานออกเป็น Excel/PDF หรือส่งเข้าอีเมลเพื่อใช้วิเคราะห์ภายนอก
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* คอนฟิกตัวเลือกการส่งออก */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white dark:bg-[#131825] p-5 rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-gray-800 dark:text-white flex items-center gap-1.5 pb-2 border-b border-gray-100 dark:border-gray-800">
              ⚙️ ตั้งค่าขอบเขตข้อมูล
            </h3>

            {/* เลือกประเภทข้อมูล */}
            <div className="space-y-2 text-xs">
              <label className="block text-[10px] uppercase font-bold text-gray-450 dark:text-gray-500">ประเภทชุดข้อมูล</label>
              <select 
                value={dataType} 
                onChange={e => setDataType(e.target.value)}
                className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl text-xs outline-none text-gray-800 dark:text-white"
              >
                <option value="revenue">💵 ข้อมูลรายรับจริง (งานเสร็จสมบูรณ์)</option>
                <option value="jobs">📅 ข้อมูลคิวงานลูกค้าและช่างทั้งหมด</option>
                <option value="employees">👥 ข้อมูลพนักงานและสิทธิ์ผู้ใช้งาน</option>
              </select>
            </div>

            {/* ป้อนช่วงเวลา (ยกเว้นพนักงาน) */}
            {dataType !== 'employees' && (
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="space-y-2">
                  <label className="block text-[10px] uppercase font-bold text-gray-450 dark:text-gray-500">ตั้งแต่วันที่</label>
                  <input 
                    type="date" 
                    value={startDate} 
                    onChange={e => setStartDate(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl text-xs outline-none text-gray-850 dark:text-white font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-[10px] uppercase font-bold text-gray-450 dark:text-gray-500">สิ้นสุดวันที่</label>
                  <input 
                    type="date" 
                    value={endDate} 
                    onChange={e => setEndDate(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl text-xs outline-none text-gray-850 dark:text-white font-mono"
                  />
                </div>
              </div>
            )}

            {/* เลือกส่งออกเป็นไฟล์ */}
            <div className="space-y-2 pt-2 text-xs">
              <label className="block text-[10px] uppercase font-bold text-gray-450 dark:text-gray-500">ดำเนินการดาวน์โหลด/พิมพ์</label>
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={handleExportExcel}
                  className="p-2.5 bg-green-600 hover:bg-green-500 text-white rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <FiDownload size={13} /> ดึงไฟล์ Excel
                </button>
                <button 
                  onClick={handlePrint}
                  className="p-2.5 bg-indigo-600 hover:bg-indigo-555 text-white rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <FiPrinter size={13} /> พิมพ์ A4 / PDF
                </button>
              </div>
            </div>

            {/* การแชร์/ส่งเข้าเมล */}
            <form onSubmit={handleSendEmail} className="space-y-3 pt-3 border-t border-gray-150 dark:border-gray-800 text-xs">
              <div className="space-y-1">
                <label className="block text-[10px] uppercase font-bold text-gray-450 dark:text-gray-500">อีเมลรับเอกสารแจ้งเตือน</label>
                <p className="text-[9px] text-gray-400 dark:text-gray-500 leading-normal">
                  ดึงอีเมลจากข้อมูลโปรไฟล์ปัจจุบันของคุณเพื่อจัดส่งไฟล์อัตโนมัติ
                </p>
              </div>
              <input 
                type="email" 
                value={recipientEmail}
                onChange={e => setRecipientEmail(e.target.value)}
                placeholder="owner@thundermaxhome.com"
                required
                className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl text-xs outline-none text-gray-850 dark:text-white font-mono"
              />
              <button 
                type="submit"
                className="w-full p-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-sm"
              >
                <FiMail size={13} /> ส่งข้อมูลรายงานเข้าเมล
              </button>
            </form>
          </div>

          {/* 📧 กล่องแสดงตัวอย่างเนื้อหาที่จะจัดส่งเข้าอีเมล */}
          <div className="bg-white dark:bg-[#131825] p-5 rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm space-y-3">
            <h3 className="text-xs font-bold text-gray-800 dark:text-white flex items-center gap-1.5 pb-2 border-b border-gray-100 dark:border-gray-800">
              📧 ตัวอย่างข้อความในอีเมล (Email Message Draft)
            </h3>
            
            <div className="space-y-2 text-xs">
              <div>
                <span className="block text-[9px] font-bold text-gray-450 uppercase">หัวข้อจดหมาย (Subject):</span>
                <span className="text-[10px] font-bold text-gray-800 dark:text-white block mt-0.5 border border-gray-100 dark:border-slate-800 p-2 rounded-lg bg-gray-50 dark:bg-gray-800/40">
                  {getEmailSubject()}
                </span>
              </div>
              <div>
                <span className="block text-[9px] font-bold text-gray-450 uppercase">เนื้อหารายงาน (Message Body):</span>
                <pre className="text-[9.5px] font-mono text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-slate-800 rounded-lg p-2.5 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto custom-scrollbar select-all">
                  {getEmailBodyText()}
                </pre>
              </div>
            </div>
          </div>
        </div>

        {/* ตารางแสดงตัวอย่างข้อมูล (Preview) */}
        <div className="lg:col-span-2 bg-white dark:bg-[#131825] p-5 rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm flex flex-col min-h-[400px]">
          <div className="flex justify-between items-center mb-3 flex-shrink-0">
            <h3 className="text-xs font-bold text-gray-855 dark:text-white">
              👁️ ตัวอย่างเนื้อหาข้อมูล ({filteredItems.length} แถว)
            </h3>
            <span className="text-[8px] bg-emerald-500/10 text-emerald-500 font-bold px-2 py-0.5 rounded-full">
              โหลดข้อมูลเสร็จสมบูรณ์
            </span>
          </div>

          <div className="flex-1 overflow-auto border border-gray-100 dark:border-gray-800 rounded-xl text-xs custom-scrollbar">
            {isLoading ? (
              <div className="flex justify-center items-center h-full text-indigo-500 font-bold animate-pulse text-xs">
                กำลังรวบรวมแถวข้อมูล...
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="flex flex-col justify-center items-center h-full text-gray-400 gap-2">
                <FiAlertCircle size={28} />
                <p>ไม่พบข้อมูลในช่วงเวลาที่เลือก</p>
              </div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50 dark:bg-[#1b2132]/40 text-gray-500 dark:text-gray-400 font-bold border-b border-gray-100 dark:border-gray-805 sticky top-0">
                  {dataType === 'revenue' && (
                    <tr>
                      <th className="p-3">รหัสงาน</th>
                      <th className="p-3">ชื่อลูกค้า</th>
                      <th className="p-3">ประเภทงาน</th>
                      <th className="p-3 text-right">ราคา</th>
                      <th className="p-3">ช่างผู้รับผิดชอบ</th>
                    </tr>
                  )}
                  {dataType === 'jobs' && (
                    <tr>
                      <th className="p-3">รหัส</th>
                      <th className="p-3">ชื่อลูกค้า</th>
                      <th className="p-3">อาการบริการ</th>
                      <th className="p-3">สถานะ</th>
                      <th className="p-3">วันที่</th>
                    </tr>
                  )}
                  {dataType === 'employees' && (
                    <tr>
                      <th className="p-3">รหัส</th>
                      <th className="p-3">ชื่อพนักงาน</th>
                      <th className="p-3">ตำแหน่ง</th>
                      <th className="p-3">ชื่อล็อกอิน</th>
                    </tr>
                  )}
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredItems.map((item, idx) => (
                    <tr key={item.id || idx} className="hover:bg-gray-50/50 dark:hover:bg-[#1b2132]/10 transition-colors">
                      {dataType === 'revenue' && (
                        <>
                          <td className="p-3 font-mono">{item.id}</td>
                          <td className="p-3 font-bold">{item.customerName}</td>
                          <td className="p-3 uppercase">{item.job_type}</td>
                          <td className="p-3 text-emerald-500 font-black text-right">฿{Number(item.price).toLocaleString()}</td>
                          <td className="p-3">{item.techName || '-'}</td>
                        </>
                      )}
                      {dataType === 'jobs' && (
                        <>
                          <td className="p-3 font-mono">{item.id}</td>
                          <td className="p-3 font-bold">{item.customerName}</td>
                          <td className="p-3 truncate max-w-[150px]">{item.problem}</td>
                          <td className="p-3 uppercase text-[10px] font-bold">{item.status}</td>
                          <td className="p-3 font-mono">{item.appointmentDate}</td>
                        </>
                      )}
                      {dataType === 'employees' && (
                        <>
                          <td className="p-3 font-mono">{item.id}</td>
                          <td className="p-3 font-bold">{item.name}</td>
                          <td className="p-3 uppercase">{item.role}</td>
                          <td className="p-3 font-mono">{item.username}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

      </div>

    </div>
  );
}
